import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { SignaturePad } from '../components/SignaturePad';
import { PhotoGallery } from '../components/PhotoGallery';
import { ExpenseTracker } from '../components/ExpenseTracker';
import { ReminderBanner } from '../components/ReminderBanner';
import { useData } from '../contexts/DataContext';
import { useT, translateWorkType } from '../lib/i18n';
import { generatePDF } from '../lib/pdf';
import { buildSignUrl, shareOrCopyUrl } from '../lib/shareLink';
import { Download, Edit2, Copy, Trash2, ArrowLeft, Printer, Building2, PenTool, Share2, Link as LinkIcon, X, CheckCircle2, Mail, Eye, BookOpen, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import { displayNumber, isNumberPending } from '../lib/numbering';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

export default function SubmissionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const {
    getSubmission,
    profile,
    updateStatus,
    removeSubmission,
    duplicateSubmission,
    ensureSignToken,
    saveSignature,
    saveTemplate,
    createInvoiceFromSubmission,
    findInvoiceForSubmission,
  } = useData();
  const { t, lang } = useT();
  const [sub, setSub] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    getSubmission(id).then((s) => {
      setSub(s);
      setSignerName(s?.client_name || '');
    });
  }, [id, getSubmission]);

  if (!sub) return <Layout><div className="p-8 text-center text-gray-500">{t('common.loading')}</div></Layout>;

  const s = sub.summary || {};
  const existingInvoice = findInvoiceForSubmission(sub.id);

  const onConvertToInvoice = async () => {
    if (existingInvoice) {
      nav(`/invoices/${existingInvoice.id}`);
      return;
    }
    setConverting(true);
    try {
      const result = await createInvoiceFromSubmission(sub.id);
      if (!result.ok) {
        if (result.error === 'already_converted' && result.existing) {
          toast.message(t('invoice.already_exists'));
          nav(`/invoices/${result.existing.id}`);
          return;
        }
        if (result.error === 'not_accepted') {
          toast.error(t('invoice.not_accepted'));
          return;
        }
        toast.error(t('invoice.convert_failed'));
        return;
      }
      toast.success(t('invoice.converted'));
      nav(`/invoices/${result.invoice.id}`);
    } finally {
      setConverting(false);
    }
  };

  const setStatus = async (status) => {
    await updateStatus(sub.id, status);
    setSub({ ...sub, status });
    toast.success('✓');
  };

  const onSignHere = async () => {
    if (!signatureData) {
      toast.error(lang === 'fr' ? 'Signature requise' : 'Signature required');
      return;
    }
    const updated = await saveSignature(sub.id, signatureData, signerName);
    if (updated) {
      setSub(updated);
      setSignOpen(false);
      toast.success(lang === 'fr' ? '✓ Signature enregistrée' : '✓ Signature saved');
    }
  };

  const onShareLink = async () => {
    if (isNumberPending(sub.submission_number)) {
      toast.error(t('common.number_pending_block_send'));
      return;
    }
    const updated = await ensureSignToken(sub.id);
    if (!updated?.sign_token) {
      toast.error(lang === 'fr' ? 'Impossible de créer le lien' : 'Could not create the link');
      return;
    }
    setSub(updated);
    const url = buildSignUrl(updated.sign_token);
    await shareOrCopyUrl({
      title: `Soumission ${updated.submission_number}`,
      text: lang === 'fr'
        ? `Voici votre soumission à signer : ${updated.client_name || ''}`
        : `Here is your quote to sign: ${updated.client_name || ''}`,
      url,
      lang,
    });
  };

  const onEmailLink = async () => {
    if (isNumberPending(sub.submission_number)) {
      toast.error(t('common.number_pending_block_send'));
      return;
    }
    if (!sub.client_email || !sub.client_email.trim()) {
      toast.error(lang === 'fr' ? 'Ajoutez un courriel au client' : 'Add a client email');
      return;
    }
    // 🌐 Offline guard: l'envoi d'email exige internet — message clair.
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — l\u2019envoi par courriel nécessite une connexion internet.'
        : 'Offline — sending by email requires an internet connection.');
      return;
    }
    const updated = await ensureSignToken(sub.id);
    if (!updated?.sign_token) {
      toast.error(lang === 'fr' ? 'Impossible de créer le lien' : 'Could not create the link');
      return;
    }
    setSub(updated);
    const url = buildSignUrl(updated.sign_token);
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    try {
      // Get the current Supabase JWT — /api/email/send-sign-link requires auth
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi pour envoyer.' : 'Your session expired. Please sign in again.');
        return;
      }
      const axios = (await import('axios')).default;
      await axios.post(`${API}/email/send-sign-link`, {
        recipient_email: sub.client_email.trim(),
        client_name: sub.client_name || '',
        company_name: profile?.company_name || '',
        submission_number: updated.submission_number,
        sign_url: url,
        lang,
        grand_total: sub.summary?.grandTotal || 0,
        misc_fees: sub.summary?.miscFees || 0,
        misc_notes: sub.summary?.miscNotes || '',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(lang === 'fr' ? '✓ Courriel envoyé au client' : '✓ Email sent to client');
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      const code = e?.response?.status;
      if (code === 401) {
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi.' : 'Your session expired.');
      } else if (code === 402) {
        toast.error(lang === 'fr' ? 'Ton essai gratuit est terminé. Souscris pour envoyer des courriels.' : 'Your free trial has ended.');
      } else if (/RESEND_API_KEY/i.test(detail)) {
        toast.error(lang === 'fr' ? 'Email non configuré (clé Resend manquante).' : 'Email not configured (Resend key missing).');
      } else {
        toast.error(lang === 'fr' ? "Échec de l\u2019envoi: " + (detail || e.message) : 'Send failed: ' + (detail || e.message));
      }
    }
  };

  const onEmailPdf = async () => {
    if (isNumberPending(sub.submission_number)) {
      toast.error(t('common.number_pending_block_send'));
      return;
    }
    if (!sub.client_email || !sub.client_email.trim()) {
      toast.error(lang === 'fr' ? 'Ajoutez un courriel au client' : 'Add a client email');
      return;
    }
    // 🌐 Offline guard: l'envoi d'email exige internet — message clair.
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — l\u2019envoi par courriel nécessite une connexion internet.'
        : 'Offline — sending by email requires an internet connection.');
      return;
    }
    const tId = toast.loading(lang === 'fr' ? 'Préparation du PDF…' : 'Preparing the PDF…');
    try {
      const blob = await generatePDF(sub, profile, t, { returnBlob: true, lang });
      if (!blob) {
        toast.dismiss(tId);
        toast.error(lang === 'fr' ? 'Échec génération PDF' : 'PDF generation failed');
        return;
      }
      // Convert blob to raw base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result || '';
          const comma = String(dataUrl).indexOf(',');
          resolve(comma > -1 ? String(dataUrl).slice(comma + 1) : String(dataUrl));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast.dismiss(tId);
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi.' : 'Your session expired.');
        return;
      }
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const axios = (await import('axios')).default;
      await axios.post(`${API}/email/send-pdf`, {
        recipient_email: sub.client_email.trim(),
        client_name: sub.client_name || '',
        company_name: profile?.company_name || '',
        submission_number: sub.submission_number,
        lang,
        grand_total: sub.summary?.grandTotal || 0,
        pdf_base64: pdfBase64,
        filename: `${sub.submission_number || 'soumission'}.pdf`,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.dismiss(tId);
      toast.success(lang === 'fr' ? '✓ PDF envoyé au client' : '✓ PDF sent to client');
    } catch (e) {
      toast.dismiss(tId);
      const detail = e?.response?.data?.detail || '';
      const code = e?.response?.status;
      if (code === 401) {
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi.' : 'Your session expired.');
      } else if (code === 402) {
        toast.error(lang === 'fr' ? 'Ton essai gratuit est terminé. Souscris pour envoyer des courriels.' : 'Your free trial has ended.');
      } else if (code === 413) {
        toast.error(lang === 'fr' ? 'PDF trop volumineux pour être envoyé.' : 'PDF too large to send.');
      } else if (/RESEND_API_KEY/i.test(detail)) {
        toast.error(lang === 'fr' ? 'Email non configuré (clé Resend manquante).' : 'Email not configured (Resend key missing).');
      } else {
        toast.error(lang === 'fr' ? "Échec de l\u2019envoi : " + (detail || e.message) : 'Send failed: ' + (detail || e.message));
      }
    }
  };

  return (
    <Layout title={displayNumber(sub.submission_number, t)}>
      <Link to="/submissions" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-4">
        <ArrowLeft size={16} /> {t('auth.back')}
      </Link>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className={`font-mono text-xs mb-1 ${isNumberPending(sub.submission_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
            {displayNumber(sub.submission_number, t)}
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">{sub.client_name}</h1>
          <div className="text-sm text-gray-500">{translateWorkType(sub.work_type, lang)}</div>
          <div className="mt-2"><StatusBadge status={sub.status} /></div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-2xl">{money(s.grandTotal)}</div>
          {sub.expires_at && (
            <div className="text-[11px] text-gray-500 mt-1">
              {t('sum.expires')} {new Date(sub.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      <ReminderBanner submission={sub} onReminded={() => getSubmission(id).then(setSub)} />

      <div className="flex flex-wrap gap-2 mb-2">
        {['draft', 'pending'].map((st) => (
          <button
            key={st}
            data-testid={`set-status-${st}`}
            onClick={() => setStatus(st)}
            className={`h-11 px-4 rounded-lg border-2 text-xs font-bold uppercase tracking-wider ${
              sub.status === st ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white' : 'border-gray-200 text-gray-600'
            }`}
          >
            {t(`status.${st}`)}
          </button>
        ))}
        {(sub.status === 'accepted' || sub.status === 'refused') && (
          <div className={`h-11 px-4 rounded-lg border-2 text-xs font-bold uppercase tracking-wider flex items-center ${
            sub.status === 'accepted'
              ? 'border-emerald-600 bg-emerald-600 text-white'
              : 'border-red-600 bg-red-600 text-white'
          }`}>
            {t(`status.${sub.status}`)}
          </div>
        )}
      </div>
      <div className="text-[10px] text-gray-500 mb-6 italic">
        {lang === 'fr'
          ? 'Le statut « Accepté » ou « Refusé » est défini automatiquement par la réponse du client.'
          : 'The "Accepted" or "Refused" status is set automatically by the client\'s response.'}
      </div>

      <div className="card p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">{t('pdf.from')}</div>
        <div className="flex items-start gap-3">
          {profile?.logo_url ? (
            <div className="w-14 h-14 rounded-xl bg-[#FAF7F2] border border-[#E5DCC9] overflow-hidden flex items-center justify-center shrink-0">
              <img src={profile.logo_url} alt="logo" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
              <Building2 size={18} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold">{profile?.company_name || (t('profile.company') + ' —')}</div>
            {profile?.address && <div className="text-sm text-gray-600">{profile.address}</div>}
            <div className="text-sm text-gray-600">
              {profile?.phone}{profile?.phone && profile?.email ? ' · ' : ''}{profile?.email}
            </div>
          </div>
        </div>
      </div>

      <div className="card p-5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">{t('pdf.to')}</div>
        <div className="font-bold">{sub.client_name}</div>
        <div className="text-sm text-gray-600">{sub.client_address}</div>
        <div className="text-sm text-gray-600">{sub.client_phone} {sub.client_email && `· ${sub.client_email}`}</div>
      </div>

      {sub.materials?.length > 0 && (
        <div className="card p-5 mb-4">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">{t('sum.materials')}</div>
          <div className="space-y-3">
            {sub.materials.map((m, i) => (
              <div key={m.id || `mat-${i}-${m.description || ''}`}>
                <div className="flex justify-between text-sm gap-3">
                  <span className="flex-1">{m.description} <span className="text-gray-400">× {m.quantity} {m.unit}</span></span>
                  <span className="font-semibold">{money((Number(m.quantity) || 0) * (Number(m.unit_price) || 0))}</span>
                </div>
                {m.note && (
                  <div className="text-[11px] text-gray-500 italic mt-1 pl-2 border-l-2 border-[#E5DCC9]">
                    ↳ {m.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-5 mb-4 space-y-2 text-sm">
        <Row label={t('sum.materials')} value={money(s.materialsTotal)} />
        <Row label={t('sum.labour')} value={money(s.labourTotal)} />
        {sub.labour?.uniform === false && Array.isArray(sub.labour?.individuals) && sub.labour.individuals.length > 0 && (
          <div className="pl-3 space-y-1 border-l-2 border-[#E5DCC9] ml-1">
            {sub.labour.individuals.map((w, i) => (
              <div key={w.id || `worker-${i}-${w.name || ''}`} className="flex justify-between text-xs text-gray-600">
                <span>{w.name || `${t('lab.worker_n')} ${i + 1}`} — {Number(w.hours) || 0}h @ {money(w.rate)}</span>
                <span className="font-semibold">{money((Number(w.rate) || 0) * (Number(w.hours) || 0))}</span>
              </div>
            ))}
          </div>
        )}
        <Row label={`${t('sum.margin')} (${s.marginPct || 0}%)`} value={money(s.marginAmount)} />
        {s.miscFees > 0 && <Row label={t('sum.misc')} value={money(s.miscFees)} />}
        {s.miscNotes && s.miscNotes.trim() && (
          <div className="bg-[#FAF7F2] border-l-4 border-[#C9A961] p-3 rounded text-xs text-gray-700 my-1">
            <div className="font-bold text-[#7A1F2B] mb-1 text-[11px] uppercase tracking-wide">
              {lang === 'fr' ? 'Détails frais divers' : 'Misc fees details'}
            </div>
            <div className="whitespace-pre-wrap leading-relaxed">{s.miscNotes}</div>
          </div>
        )}
        <div className="h-px bg-gray-200 my-1" />
        <Row label={t('sum.subtotal')} value={money(s.subtotal)} bold />
        {(s.taxes || []).map((tx) => <Row key={tx.label} label={tx.label} value={money(tx.amount)} muted />)}
        <div className="flex items-center justify-between pt-3 border-t-2 border-gray-200">
          <span className="font-heading font-black text-lg">{t('sum.grand')}</span>
          <span className="font-heading font-black text-2xl">{money(s.grandTotal)}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          data-testid="preview-pdf-btn"
          onClick={async () => {
            const tId = toast.loading(lang === 'fr' ? 'Génération de l\u2019aperçu…' : 'Generating preview…');
            const ok = await generatePDF(sub, profile, t, { preview: true, lang });
            toast.dismiss(tId);
            if (!ok) toast.error(lang === 'fr' ? 'Échec de l\u2019aperçu' : 'Preview failed');
          }}
          className="btn-accent gap-2 col-span-2"
        >
          <Eye size={18} /> {lang === 'fr' ? 'Voir mon PDF (aperçu)' : 'Preview my PDF'}
        </button>
        <button data-testid="download-pdf" onClick={() => generatePDF(sub, profile, t, { lang })} className="btn-primary gap-2">
          <Download size={18} /> PDF
        </button>
        <button data-testid="print-btn" onClick={() => generatePDF(sub, profile, t, { print: true, lang })} className="btn-primary gap-2">
          <Printer size={18} /> {t('common.print')}
        </button>
        {sub.client_email && (
          <button
            data-testid="email-pdf-btn"
            onClick={onEmailPdf}
            className="btn-primary gap-2 col-span-2"
          >
            <Mail size={18} /> {lang === 'fr' ? 'Envoyer le PDF par courriel' : 'Email the PDF'}
          </button>
        )}
        {sub.status === 'accepted' && (
          <button
            data-testid="convert-invoice-btn"
            onClick={onConvertToInvoice}
            disabled={converting}
            className="btn-accent gap-2 col-span-2"
          >
            <Receipt size={18} />
            {converting
              ? t('common.loading')
              : existingInvoice
                ? t('invoice.view')
                : t('invoice.convert')}
          </button>
        )}
        {!sub.signed_at && (
          <>
            <button data-testid="sign-here-btn" onClick={() => setSignOpen(true)} className="btn-accent gap-2 col-span-2">
              <PenTool size={18} /> {lang === 'fr' ? 'Faire signer le client' : 'Get client signature'}
            </button>
            <button data-testid="share-sign-link-btn" onClick={onShareLink} className="btn-outline gap-2 col-span-2">
              <Share2 size={18} /> {lang === 'fr' ? 'Envoyer un lien à signer' : 'Send signing link'}
            </button>
            {sub.client_email && (
              <button data-testid="email-sign-link-btn" onClick={onEmailLink} className="btn-outline gap-2 col-span-2">
                <Mail size={18} /> {lang === 'fr' ? 'Envoyer le lien de signature par courriel' : 'Email the signing link'}
              </button>
            )}
          </>
        )}
        <Link to={`/submissions/${sub.id}/edit`} data-testid="edit-btn" className="btn-outline gap-2">
          <Edit2 size={18} /> {t('hist.edit')}
        </Link>
        <button
          data-testid="duplicate-btn"
          onClick={async () => { const c = await duplicateSubmission(sub.id); if (c) nav(`/submissions/${c.id}`); }}
          className="btn-outline gap-2"
        >
          <Copy size={18} /> {t('hist.duplicate')}
        </button>
        <button
          data-testid="save-as-template-btn"
          onClick={() => { setTplName(sub.work_type || ''); setTplOpen(true); }}
          className="btn-outline gap-2 col-span-2"
        >
          <BookOpen size={18} /> {lang === 'fr' ? 'Sauvegarder comme modèle' : 'Save as template'}
        </button>
        <button
          data-testid="delete-btn"
          onClick={async () => {
            if (!window.confirm(t('hist.delete') + ' ?')) return;
            await removeSubmission(sub.id);
            nav('/submissions');
          }}
          className="btn-outline gap-2 !text-red-700 !border-red-300 col-span-2"
        >
          <Trash2 size={18} /> {t('hist.delete')}
        </button>
      </div>

      {sub.signed_at && sub.signature_data_url && (
        <div className="card border-2 border-emerald-200 bg-emerald-50 p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="text-emerald-700" size={20} />
            <div>
              <div className="font-bold text-emerald-900">
                {lang === 'fr' ? 'Soumission signée' : 'Quote signed'}
              </div>
              <div className="text-xs text-emerald-800">
                {lang === 'fr' ? 'Par' : 'By'} <b>{sub.signed_by_name || sub.client_name}</b> · {new Date(sub.signed_at).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-emerald-200 p-2">
            <img src={sub.signature_data_url} alt="signature" className="w-full max-h-[120px] object-contain" />
          </div>
        </div>
      )}

      <div className="mb-4">
        <PhotoGallery submission={sub} onUpdate={() => getSubmission(id).then(setSub)} />
      </div>

      <div className="mb-4">
        <ExpenseTracker submission={sub} onUpdate={() => getSubmission(id).then(setSub)} />
      </div>

      {signOpen && (
        <div
          data-testid="sign-modal"
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setSignOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between">
              <div className="font-heading font-black text-xl">
                {lang === 'fr' ? 'Signature du client' : 'Client signature'}
              </div>
              <button onClick={() => setSignOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <label className="label">{lang === 'fr' ? 'Nom complet' : 'Full name'}</label>
              <input
                data-testid="signer-name"
                className="field"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
              />
              <SignaturePad onChange={setSignatureData} label={lang === 'fr' ? 'Signature' : 'Signature'} height={180} />
              <button
                data-testid="confirm-signature"
                onClick={onSignHere}
                disabled={!signatureData}
                className="btn-primary w-full disabled:opacity-50"
              >
                {lang === 'fr' ? 'Confirmer la signature' : 'Confirm signature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tplOpen && (
        <div
          data-testid="save-template-modal"
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setTplOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between bg-[#FAF7F2]">
              <div className="font-heading font-black text-lg">
                {lang === 'fr' ? 'Sauvegarder comme modèle' : 'Save as template'}
              </div>
              <button onClick={() => setTplOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">
                {lang === 'fr'
                  ? 'Sauvegarde la liste des matériaux + main-d\u2019œuvre comme modèle réutilisable. Le client et l\u2019adresse ne sont PAS enregistrés.'
                  : 'Save the material + labour list as a reusable template. Client and address are NOT saved.'}
              </p>
              <label className="label">{lang === 'fr' ? 'Nom du modèle' : 'Template name'}</label>
              <input
                data-testid="template-name-input"
                className="field"
                placeholder={lang === 'fr' ? 'ex: Rénovation salle de bain' : 'e.g. Bathroom renovation'}
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                autoFocus
              />
              <button
                data-testid="confirm-save-template"
                onClick={async () => {
                  const name = (tplName || '').trim();
                  if (!name) {
                    toast.error(lang === 'fr' ? 'Donne un nom au modèle' : 'Give the template a name');
                    return;
                  }
                  const saved = await saveTemplate({
                    name,
                    work_type: sub.work_type,
                    province: sub.province,
                    materials: sub.materials,
                    labour: sub.labour,
                    margin: sub.summary?.marginPct,
                    miscFees: sub.summary?.miscFees,
                    miscNotes: sub.summary?.miscNotes,
                    notes: sub.notes,
                  });
                  if (saved) {
                    toast.success(lang === 'fr' ? '✓ Modèle sauvegardé' : '✓ Template saved');
                    setTplOpen(false);
                    setTplName('');
                  } else {
                    toast.error(lang === 'fr' ? 'Échec sauvegarde' : 'Save failed');
                  }
                }}
                className="btn-primary w-full"
              >
                {lang === 'fr' ? 'Sauvegarder' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Row({ label, value, bold, muted }) {
  return (
    <div className={`flex justify-between ${muted ? 'text-gray-600' : ''} ${bold ? 'font-bold' : ''}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}
